import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Ban, UserCheck, UserX, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface Profile {
  id: string;
  username: string;
  display_name: string | null;
  profile_image_url: string | null;
  email: string | null;
  is_banned: boolean;
  banned_at: string | null;
  ban_reason: string | null;
  banned_by: string | null;
  created_at: string;
  last_sign_in_at: string | null;
}

export const UserManagement = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [banReason, setBanReason] = useState('');
  const [actionType, setActionType] = useState<'ban' | 'unban'>('ban');

  // Fetch users with search
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users', searchTerm],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_users_with_auth_data');
      if (error) throw error;
      
      let filteredData = data as Profile[];
      
      // Apply search filter if searchTerm exists
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filteredData = filteredData.filter(user => 
          user.username?.toLowerCase().includes(searchLower) ||
          user.display_name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower)
        );
      }
      
      return filteredData.slice(0, 50); // Limit to 50 results
    },
    enabled: !!user,
  });

  // Ban/Unban user mutation
  const banMutation = useMutation({
    mutationFn: async ({ userId, ban, reason }: { userId: string; ban: boolean; reason?: string }) => {
      const updateData: any = {
        is_banned: ban,
        banned_at: ban ? new Date().toISOString() : null,
        ban_reason: ban ? reason : null,
        banned_by: ban ? user?.id : null,
      };

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId);

      if (error) throw error;
    },
    onSuccess: (_, { ban }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(ban ? 'User banned successfully' : 'User unbanned successfully');
      setShowBanDialog(false);
      setSelectedUser(null);
      setBanReason('');
    },
    onError: (error) => {
      console.error('Error updating user ban status:', error);
      toast.error('Failed to update user status');
    },
  });

  const handleBanAction = (user: Profile, action: 'ban' | 'unban') => {
    setSelectedUser(user);
    setActionType(action);
    setShowBanDialog(true);
  };

  const confirmBanAction = () => {
    if (!selectedUser) return;
    banMutation.mutate({
      userId: selectedUser.id,
      ban: actionType === 'ban',
      reason: actionType === 'ban' ? banReason : undefined,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users by username, display name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="bg-card rounded-lg border">
        <div className="p-4 border-b">
          <h3 className="font-semibold">User Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage user accounts and ban status
          </p>
        </div>

        <div className="divide-y">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">
              Loading users...
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No users found
            </div>
          ) : (
            users.map((profile) => (
              <div key={profile.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={profile.profile_image_url || ''} />
                    <AvatarFallback>
                      {(profile.display_name?.charAt(0) || profile.username?.charAt(0) || 'U').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">
                        {profile.display_name || profile.username}
                      </p>
                      {profile.is_banned && (
                        <Badge variant="destructive" className="text-xs">
                          Banned
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      @{profile.username} • {profile.email}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">
                        Last login: {profile.last_sign_in_at 
                          ? formatDistanceToNow(new Date(profile.last_sign_in_at), { addSuffix: true })
                          : 'Never'
                        }
                      </p>
                    </div>
                    {profile.is_banned && profile.ban_reason && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Reason: {profile.ban_reason}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {profile.is_banned ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBanAction(profile, 'unban')}
                      disabled={banMutation.isPending}
                    >
                      <UserCheck className="h-4 w-4 mr-1" />
                      Unban
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleBanAction(profile, 'ban')}
                      disabled={banMutation.isPending}
                    >
                      <Ban className="h-4 w-4 mr-1" />
                      Ban
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'ban' ? 'Ban User' : 'Unban User'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'ban' 
                ? `Are you sure you want to ban @${selectedUser?.username}? They will not be able to log in until unbanned.`
                : `Are you sure you want to unban @${selectedUser?.username}? They will be able to log in again.`
              }
            </DialogDescription>
          </DialogHeader>

          {actionType === 'ban' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Reason for ban (optional)</label>
              <Textarea
                placeholder="Enter reason for banning this user..."
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                rows={3}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBanDialog(false)}
              disabled={banMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant={actionType === 'ban' ? 'destructive' : 'default'}
              onClick={confirmBanAction}
              disabled={banMutation.isPending}
            >
              {banMutation.isPending ? 'Processing...' : (actionType === 'ban' ? 'Ban User' : 'Unban User')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};